/**
 * Recovery from a stale build.
 *
 * Every deploy publishes freshly hashed bundles and Firebase Hosting drops the
 * previous ones. A tab (or an installed PWA) that has been open across a deploy
 * still runs the OLD main bundle, so the first lazy route it loads requests a
 * chunk that no longer exists. Hosting rewrites the missing path to index.html,
 * webpack receives HTML where it expected JavaScript, and the user gets
 * "אופס! משהו השתבש" — for what is really just an outdated tab.
 *
 * index.html is served no-cache, so a single reload picks up the new build.
 */

const RELOAD_MARK = 'kscalc:staleBuildReloadAt'
// A reload only helps once. If the same failure comes back immediately after
// one, the chunk is missing for another reason and reloading would spin.
const RELOAD_COOLDOWN_MS = 30000

export function isStaleBuildError(error) {
  if (!error) return false
  if (error.name === 'ChunkLoadError') return true
  const message = String(error.message || error)
  return /Loading (CSS )?chunk .* failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)
}

/**
 * Reload once to pick up the current build. Returns true if the reload was
 * triggered, false when one already happened moments ago (so the caller can
 * show the manual fallback instead of looping).
 */
export function reloadForStaleBuild({
  now = Date.now(),
  storage = safeSessionStorage(),
  reload = () => window.location.reload(),
} = {}) {
  const last = Number(storage?.getItem(RELOAD_MARK) || 0)
  if (last && now - last < RELOAD_COOLDOWN_MS) return false

  try {
    storage?.setItem(RELOAD_MARK, String(now))
  } catch {
    // A blocked sessionStorage costs us the loop guard, not the recovery.
  }
  reload()
  return true
}

function safeSessionStorage() {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}
